import { defaultDropAnimationSideEffects } from '@dnd-kit/core'

import { arrayToMap } from 'lib/array/arrayUtils'
import type { TripItineraryItem, TripItemWithoutDay, TripStop } from 'tripPlanner/api/types'
import {
  INTERACTIVE_DRAG_IGNORE_SELECTORS,
  createDragEventSensors,
  getDropPointFromDragEvent,
  getPointerCoordinates,
  moveItemToDropTarget,
  type PointerCoordinates,
} from './tripDragReorder'
import { canMoveTripItemToAnotherDay, type TripMovePlacement } from './tripMoveDayOptions'

export { getDropPointFromDragEvent, getPointerCoordinates }
export type { PointerCoordinates }

export type TimelineItem = TripItineraryItem | TripItemWithoutDay

// A small pointer buffer keeps taps opening the item card while a deliberate drag
// still activates quickly. The card body is itself clickable, so 1px (as the list
// uses) would be too twitchy here.
export const POINTER_DRAG_ACTIVATION_DISTANCE = 6
export const TOUCH_DRAG_ACTIVATION_DELAY = 250
export const TOUCH_DRAG_ACTIVATION_TOLERANCE = 5
export const DROP_ANIMATION_DURATION = 150

const TIMELINE_DRAG_DISABLED_SELECTOR = [
  '[data-timeline-drag-disabled="true"]',
  ...INTERACTIVE_DRAG_IGNORE_SELECTORS,
].join(',')

const timelineSensors = createDragEventSensors(TIMELINE_DRAG_DISABLED_SELECTOR)
export const TimelinePointerSensor = timelineSensors.PointerSensor
export const TimelineTouchSensor = timelineSensors.TouchSensor

export const timelineDropAnimationSideEffects = defaultDropAnimationSideEffects({
  styles: {
    active: {
      opacity: '0.35',
    },
  },
})

export const timelineAutoScrollConfig = {
  threshold: {
    x: 0,
    y: 0.2,
  },
}

export function canDragTimelineItem(item: TimelineItem): boolean {
  return canMoveTripItemToAnotherDay(item)
}

export interface TimelineItemLocation {
  stopId: number
  dayId: number | undefined
  itemIndex: number
}

interface FlattenedTimelineItem {
  item: TimelineItem
  location: TimelineItemLocation
}

function flattenTimelineItems(stops: ReadonlyArray<TripStop>): Array<FlattenedTimelineItem> {
  const items: Array<FlattenedTimelineItem> = []
  for (const stop of stops) {
    for (const day of stop.days ?? []) {
      const dayItems = day.items ?? []
      dayItems.forEach((item, itemIndex) => {
        items.push({ item, location: { stopId: stop.id, dayId: day.id, itemIndex } })
      })
    }
    const itemsWithoutDay = stop.itemsWithoutDay ?? []
    itemsWithoutDay.forEach((item, itemIndex) => {
      items.push({ item, location: { stopId: stop.id, dayId: undefined, itemIndex } })
    })
  }
  return items
}

/** Maps every itinerary item id to its current stop + day (day undefined = unscheduled). */
export function buildTimelineItemLocationById(stops: ReadonlyArray<TripStop>): Map<number, TimelineItemLocation> {
  return arrayToMap(flattenTimelineItems(stops), ({ item }) => item.id, ({ location }) => location)
}

export function buildTimelineItemById(stops: ReadonlyArray<TripStop>): Map<number, TimelineItem> {
  return arrayToMap(flattenTimelineItems(stops), ({ item }) => item.id, ({ item }) => item)
}

export type TimelineDropPlacement = 'before' | 'after'

export type TimelineDropTarget =
  | { type: 'item'; itemId: number; placement: TimelineDropPlacement }
  | { type: 'day'; dayId: number }
  | { type: 'unplanned'; stopId: number }

export type TimelineMovePlacement = TripMovePlacement

/** The resolved move to send to the API once an item is dropped. */
export interface TimelineMove {
  target: { targetType: 'day'; dayId: number } | { targetType: 'unplanned' }
  placement: TimelineMovePlacement
}

const TIMELINE_DAY_SECTION_SELECTOR = '[data-itinerary-day-section="true"]'
const TIMELINE_UNPLANNED_SECTION_SELECTOR = '[data-itinerary-unplanned-section="true"]'
const TIMELINE_ITEM_SELECTOR = '[data-timeline-item-id]'

/**
 * Resolves the drop target under a viewport point. An item tile (deepest match)
 * yields a before/after insertion relative to that item; otherwise the enclosing
 * day section yields an append target. Points over the dragged item itself resolve
 * to no target so a self-drop never becomes a spurious day move.
 */
export function getTimelineDropTargetFromPoint(
  coordinates: PointerCoordinates,
  ignoredItemId: number | undefined,
): TimelineDropTarget | undefined {
  for (const element of document.elementsFromPoint(coordinates.x, coordinates.y)) {
    const itemElement = element.closest(TIMELINE_ITEM_SELECTOR)
    const itemIdAttr = itemElement?.getAttribute('data-timeline-item-id')
    if (itemElement && itemIdAttr) {
      const itemId = Number(itemIdAttr)
      if (!Number.isFinite(itemId)) continue
      if (itemId === ignoredItemId) return undefined

      const rect = itemElement.getBoundingClientRect()
      return {
        type: 'item',
        itemId,
        placement: coordinates.y < rect.top + rect.height / 2 ? 'before' : 'after',
      }
    }

    const daySection = element.closest(TIMELINE_DAY_SECTION_SELECTOR)
    const dayIdAttr = daySection?.getAttribute('data-day-id')
    if (dayIdAttr) {
      const dayId = Number(dayIdAttr)
      if (Number.isFinite(dayId)) return { type: 'day', dayId }
    }

    const unplannedSection = element.closest(TIMELINE_UNPLANNED_SECTION_SELECTOR)
    const stopIdAttr = unplannedSection?.getAttribute('data-stop-id')
    if (stopIdAttr) {
      const stopId = Number(stopIdAttr)
      if (Number.isFinite(stopId)) return { type: 'unplanned', stopId }
    }
  }

  return undefined
}

export function isSameTimelineDropTarget(first: TimelineDropTarget | undefined, second: TimelineDropTarget | undefined): boolean {
  if (!first || !second) return first === second
  if (first.type === 'item' && second.type === 'item') return first.itemId === second.itemId && first.placement === second.placement
  if (first.type === 'day' && second.type === 'day') return first.dayId === second.dayId
  if (first.type === 'unplanned' && second.type === 'unplanned') return first.stopId === second.stopId
  return false
}

function isAdjacentNoOpDrop(activeLocation: TimelineItemLocation, targetLocation: TimelineItemLocation, placement: TimelineDropPlacement): boolean {
  if (activeLocation.stopId !== targetLocation.stopId || activeLocation.dayId !== targetLocation.dayId) return false
  if (placement === 'before') return activeLocation.itemIndex === targetLocation.itemIndex - 1
  return activeLocation.itemIndex === targetLocation.itemIndex + 1
}

/**
 * Maps a resolved drop target to an API move. Returns undefined for no-ops
 * (dropping on itself, or appending to the day the item already lives in).
 */
export function buildTimelineMove(
  activeItemId: number,
  target: TimelineDropTarget,
  sourceDayId: number | undefined,
  itemLocationById: ReadonlyMap<number, TimelineItemLocation>,
): TimelineMove | undefined {
  if (target.type === 'item') {
    if (target.itemId === activeItemId) return undefined

    const activeLocation = itemLocationById.get(activeItemId)
    const location = itemLocationById.get(target.itemId)
    if (!activeLocation || !location) return undefined
    if (isAdjacentNoOpDrop(activeLocation, location, target.placement)) return undefined

    const placement: TimelineMovePlacement = { placementType: target.placement, itemId: target.itemId }
    return location.dayId !== undefined
      ? { target: { targetType: 'day', dayId: location.dayId }, placement }
      : { target: { targetType: 'unplanned' }, placement }
  }

  if (target.type === 'unplanned') {
    const activeLocation = itemLocationById.get(activeItemId)
    if (activeLocation?.stopId === target.stopId && activeLocation.dayId === undefined) return undefined
    return { target: { targetType: 'unplanned' }, placement: { placementType: 'end' } }
  }

  if (target.dayId === sourceDayId) return undefined
  return { target: { targetType: 'day', dayId: target.dayId }, placement: { placementType: 'end' } }
}

// ── Optimistic reorder ──
//
// The move API only invalidates queries on success (no optimistic cache write), so
// the canvas would otherwise wait a full round-trip before the item visibly moves.
// Mirroring the list view, we compute an optimistic copy of the stops with the
// dragged item relocated, render from that immediately, and roll back if the move
// fails. Because the day view renders items in array order, moving the item in the
// array is all that is needed for the drop to land where the insertion line showed.

function removeItemFromStops(
  stops: ReadonlyArray<TripStop>,
  itemId: number,
): { stops: Array<TripStop>; removed: { type: 'day'; item: TripItineraryItem } | { type: 'unplanned'; item: TripItemWithoutDay } | undefined } {
  let removed: { type: 'day'; item: TripItineraryItem } | { type: 'unplanned'; item: TripItemWithoutDay } | undefined

  const nextStops = stops.map(stop => {
    let stopChanged = false

    const days = (stop.days ?? []).map(day => {
      const items = day.items ?? []
      const match = items.find(item => item.id === itemId)
      if (!match) return day
      removed = { type: 'day', item: match }
      stopChanged = true
      return { ...day, items: items.filter(item => item.id !== itemId) }
    })

    const itemsWithoutDay = stop.itemsWithoutDay ?? []
    const withoutDayMatch = itemsWithoutDay.find(item => item.id === itemId)
    let nextItemsWithoutDay = itemsWithoutDay
    if (withoutDayMatch) {
      removed = { type: 'unplanned', item: withoutDayMatch }
      stopChanged = true
      nextItemsWithoutDay = itemsWithoutDay.filter(item => item.id !== itemId)
    }

    if (!stopChanged) return stop
    return { ...stop, days, itemsWithoutDay: nextItemsWithoutDay }
  })

  return { stops: nextStops, removed }
}

function moveItemIntoTimelineItems<T extends TimelineItem>(items: Array<T>, item: T, targetItem: T, placement: TimelineDropPlacement): Array<T> {
  return moveItemToDropTarget(items, item, targetItem, placement)
}

/**
 * Returns a copy of `stops` with `activeItemId` moved to `target`, or the original
 * `stops` if the move can't be represented optimistically (item/target not found).
 */
export function applyOptimisticTimelineMove(
  stops: ReadonlyArray<TripStop>,
  activeItemId: number,
  target: TimelineDropTarget,
): Array<TripStop> {
  const { stops: withoutActive, removed } = removeItemFromStops(stops, activeItemId)
  if (!removed) return [...stops]

  let inserted = false

  if (target.type === 'item') {
    const next = withoutActive.map(stop => {
      const dayIndex = (stop.days ?? []).findIndex(day => (day.items ?? []).some(item => item.id === target.itemId))
      if (dayIndex >= 0) {
        const day = stop.days[dayIndex]
        const targetItem = day.items.find(item => item.id === target.itemId)
        if (!targetItem) return stop
        const days = [...stop.days]
        days[dayIndex] = { ...day, items: moveItemIntoTimelineItems(day.items, removed.item, targetItem, target.placement) }
        inserted = true
        return { ...stop, days }
      }

      const withoutDay = stop.itemsWithoutDay ?? []
      const targetItem = withoutDay.find(item => item.id === target.itemId)
      if (targetItem) {
        inserted = true
        return { ...stop, itemsWithoutDay: moveItemIntoTimelineItems(withoutDay, removed.item, targetItem, target.placement) }
      }

      return stop
    })
    // If the anchor item vanished (a stale target), keep the original order rather
    // than dropping the removed item entirely.
    return inserted ? next : [...stops]
  }

  const next = withoutActive.map(stop => {
    if (target.type === 'unplanned') {
      if (stop.id !== target.stopId) return stop
      inserted = true
      return { ...stop, itemsWithoutDay: [...(stop.itemsWithoutDay ?? []), removed.item] }
    }

    const dayIndex = (stop.days ?? []).findIndex(day => day.id === target.dayId)
    if (dayIndex < 0) return stop
    const day = stop.days[dayIndex]
    const days = [...stop.days]
    days[dayIndex] = { ...day, items: [...(day.items ?? []), removed.item] }
    inserted = true
    return { ...stop, days }
  })
  return inserted ? next : [...stops]
}
